import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { OrderTimelineDialog } from "@/components/OrderTimelineDialog";
import { Download, FileWarning, ClipboardList, FileCheck2, LifeBuoy, History } from "lucide-react";

export async function downloadOrderDocument(order) {
  try {
    const response = await api.get(`/orders/${order.id}/document`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = order.original_filename || "document";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error("Document download failed.");
  }
}

export async function downloadFinishedReport(order) {
  try {
    const response = await api.get(`/orders/${order.id}/report`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = order.report_original_filename || "report";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error("Report download failed.");
  }
}

async function openInlineBlob(path, fallbackMessage) {
  try {
    const response = await api.get(path, { responseType: "blob" });
    const contentType = response.headers?.["content-type"] || "application/octet-stream";
    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const opened = window.open(url, "_blank", "noopener");
    if (!opened) toast.error("Pop-up blocked — allow pop-ups to view the file in a new tab.");
    setTimeout(() => window.URL.revokeObjectURL(url), 120000);
  } catch (error) {
    toast.error(fallbackMessage);
  }
}

export async function viewOrderDocument(order) {
  await openInlineBlob(`/orders/${order.id}/document?inline=true`, "Could not open the document.");
}

export async function viewFinishedReport(order) {
  await openInlineBlob(`/orders/${order.id}/report?inline=true`, "Could not open the report.");
}

export const OrdersTable = ({ orders, onReview }) => {
  const navigate = useNavigate();
  const [timelineOrder, setTimelineOrder] = useState(null);
  return (
  <Card className="bg-card border-border" data-testid="orders-table-card">
    <CardHeader>
      <CardTitle className="font-display text-xl flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-primary" /> Your report requests
      </CardTitle>
      <CardDescription>
        Verified and customer-confirmed requests are eligible for checkout. Mismatched requests need your review.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {orders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-10 text-center" data-testid="orders-empty-state">
          <p className="text-sm font-medium">No report requests yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Submit your first matter with the Order Your QSIP form.
          </p>
        </div>
      ) : (
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="text-xs uppercase tracking-wide">Order</TableHead>
                <TableHead scope="col" className="text-xs uppercase tracking-wide">Type</TableHead>
                <TableHead scope="col" className="text-xs uppercase tracking-wide">Identifier</TableHead>
                <TableHead scope="col" className="text-xs uppercase tracking-wide">Verification</TableHead>
                <TableHead scope="col" className="text-xs uppercase tracking-wide">Payment</TableHead>
                <TableHead scope="col" className="text-xs uppercase tracking-wide">Operations</TableHead>
                <TableHead scope="col" className="text-xs uppercase tracking-wide text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} data-testid={`orders-table-row-${order.id}`} className="hover:bg-secondary/40">
                  <TableCell className="font-mono text-xs text-muted-foreground">{order.id.slice(0, 8)}</TableCell>
                  <TableCell className="text-sm capitalize">
                    {order.request_type.replaceAll("_", " ")}
                    {order.purchase_type === "single" && (
                      <span className="ml-1.5 rounded border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary uppercase">
                        Single
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{order.identifier_value}</TableCell>
                  <TableCell>
                    <StatusBadge status={order.verification_status} testid={`order-${order.id}-verification-badge`} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={order.report_paid ? "paid" : "unpaid"} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={order.admin_status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {order.report_filename && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[hsl(var(--status-verified)/0.4)] text-[hsl(var(--status-verified))] hover:bg-[hsl(var(--status-verified)/0.1)]"
                          data-testid={`order-${order.id}-report-download-button`}
                          onClick={() => downloadFinishedReport(order)}
                        >
                          <FileCheck2 className="h-3.5 w-3.5 mr-1" /> Report
                        </Button>
                      )}
                      {(order.verification_status === "mismatched" ||
                        (order.verification_status === "verified" && !order.customer_verified_at)) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className={
                            order.verification_status === "mismatched"
                              ? "border-[hsl(var(--status-mismatch)/0.4)] text-[hsl(var(--status-mismatch))] hover:bg-[hsl(var(--status-mismatch)/0.1)]"
                              : "border-primary/40 text-primary hover:bg-primary/10"
                          }
                          data-testid={`order-${order.id}-review-button`}
                          onClick={() => onReview(order)}
                        >
                          <FileWarning className="h-3.5 w-3.5 mr-1" /> Review &amp; confirm
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Track order progress"
                        data-testid={`order-${order.id}-timeline-button`}
                        onClick={() => setTimelineOrder(order)}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Get help with this order"
                        data-testid={`order-${order.id}-help-button`}
                        onClick={() => navigate(`/support?order=${order.id}`)}
                      >
                        <LifeBuoy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Download document"
                        data-testid={`order-${order.id}-download-button`}
                        onClick={() => downloadOrderDocument(order)}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      )}
    </CardContent>
    <OrderTimelineDialog
      order={timelineOrder}
      open={Boolean(timelineOrder)}
      onOpenChange={(open) => !open && setTimelineOrder(null)}
    />
  </Card>
  );
};
